import { useMemo, useState } from "react";
import { format, differenceInDays, addDays, startOfDay, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut } from "lucide-react";

const stageColors = [
  "#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

const normalizeStage = (name) =>
  (name || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Mapa fixo de cores para as etapas principais (cores unicas garantidas).
// Etapas fora do mapa recebem uma cor estavel via hash do nome.
const STAGE_COLOR_MAP = {
  "corte": "#3b82f6",
  "corte de material": "#06b6d4",
  "corte de tubo": "#10b981",
  "solda": "#f59e0b",
  "soldagem": "#f97316",
  "montagem": "#6366f1",
  "montagem final": "#ec4899",
  "teste de pressao": "#8b5cf6",
};

// Cor fixa por nome de etapa - nunca muda quando etapas entram/saem
const colorForStage = (name) => {
  const key = normalizeStage(name);
  if (STAGE_COLOR_MAP[key]) return STAGE_COLOR_MAP[key];
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return stageColors[hash % stageColors.length];
};

const statusLabels = {
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  concluido: "Concluido",
};

const ZOOM_LEVELS = [
  { label: "Dia", pxPerDay: 80 },
  { label: "Semana", pxPerDay: 40 },
  { label: "Mes", pxPerDay: 18 },
  { label: "Trimestre", pxPerDay: 6 },
];

export default function GanttChart({ orders }) {
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(1);
  const pxPerDay = ZOOM_LEVELS[zoomIdx].pxPerDay;

  // Safety cap to prevent browser OOM in production when there are hundreds of orders
  const MAX_ORDERS_IN_GANTT = 80;
  const MAX_TIMELINE_DAYS = 730; // 2 years

  const { ganttOrders, timelineStart, timelineEnd, totalDays, uniqueStageNames, truncated } = useMemo(() => {
    const validOrders = orders.filter((o) => {
      const hasStart = o.start_date && isValid(new Date(o.start_date));
      const hasDeadline = o.deadline && isValid(new Date(o.deadline));
      return (hasStart || hasDeadline) && !["cancelado", "concluido"].includes(o.status);
    });

    const wasTruncated = validOrders.length > MAX_ORDERS_IN_GANTT;
    const cappedOrders = wasTruncated ? validOrders.slice(0, MAX_ORDERS_IN_GANTT) : validOrders;

    if (!cappedOrders.length) return { ganttOrders: [], timelineStart: new Date(), timelineEnd: new Date(), totalDays: 0, uniqueStageNames: [], truncated: false };

    const allStageNames = new Set();
    cappedOrders.forEach((o) => (o.stages || []).forEach((s) => allStageNames.add(s.name)));

    let minDate = null;
    let maxDate = null;
    cappedOrders.forEach((o) => {
      const start = o.start_date ? startOfDay(new Date(o.start_date)) : null;
      const end = o.deadline ? startOfDay(new Date(o.deadline)) : null;
      const created = startOfDay(new Date(o.created_at));
      const effectiveStart = start || created;
      const effectiveEnd = end || addDays(effectiveStart, 30);
      if (!minDate || effectiveStart < minDate) minDate = effectiveStart;
      if (!maxDate || effectiveEnd > maxDate) maxDate = effectiveEnd;
      // Also check sub-stage dates for accurate timeline range
      (o.stages || []).forEach((s) => {
        (s.sub_stages || []).forEach((ss) => {
          if (ss.start_date) {
            const d = startOfDay(new Date(ss.start_date));
            if (isValid(d)) { if (!minDate || d < minDate) minDate = d; }
          }
          if (ss.deadline) {
            const d = startOfDay(new Date(ss.deadline));
            if (isValid(d)) { if (!maxDate || d > maxDate) maxDate = d; }
          }
        });
      });
    });

    minDate = addDays(minDate, -1);
    maxDate = addDays(maxDate, 1);
    let days = Math.max(differenceInDays(maxDate, minDate), 7);
    // Cap timeline span to avoid runaway rendering when a single order has an
    // absurd deadline (e.g., year 2099) that would blow up the DOM.
    if (days > MAX_TIMELINE_DAYS) {
      days = MAX_TIMELINE_DAYS;
      maxDate = addDays(minDate, MAX_TIMELINE_DAYS);
    }

    const stageNameList = [...allStageNames];
    const processed = cappedOrders.map((o) => {
      const created = startOfDay(new Date(o.created_at));
      const start = o.start_date ? startOfDay(new Date(o.start_date)) : created;
      const end = o.deadline ? startOfDay(new Date(o.deadline)) : addDays(start, 30);
      const orderDays = Math.max(differenceInDays(end, start), 1);
      const stages = o.stages || [];
      const totalStages = stages.length || 1;
      const daysPerStage = orderDays / totalStages;

      const stagesBars = stages.map((s, idx) => {
        // Try to get real dates from sub-stages first
        const subDates = (s.sub_stages || [])
          .filter((ss) => ss.start_date || ss.deadline)
          .map((ss) => ({
            start: ss.start_date ? startOfDay(new Date(ss.start_date)) : null,
            end: ss.deadline ? startOfDay(new Date(ss.deadline)) : null,
          }));

        let stageStart, stageEnd;
        if (subDates.length > 0) {
          // Use real sub-stage dates: earliest start, latest end
          const starts = subDates.filter((d) => d.start).map((d) => d.start);
          const ends = subDates.filter((d) => d.end).map((d) => d.end);
          stageStart = starts.length > 0 ? new Date(Math.min(...starts)) : null;
          stageEnd = ends.length > 0 ? new Date(Math.max(...ends)) : null;
          // Fallback if only one side is available
          if (!stageStart) stageStart = stageEnd ? addDays(stageEnd, -1) : addDays(start, Math.round(idx * daysPerStage));
          if (!stageEnd) stageEnd = addDays(stageStart, 1);
        } else {
          // No sub-stage dates: proportional distribution
          stageStart = addDays(start, Math.round(idx * daysPerStage));
          stageEnd = addDays(start, Math.round((idx + 1) * daysPerStage));
        }

        return {
          ...s,
          startOffset: differenceInDays(stageStart, minDate),
          width: Math.max(differenceInDays(stageEnd, stageStart), 1),
          color: colorForStage(s.name),
        };
      });

      return {
        ...o,
        effectiveStart: start,
        effectiveEnd: end,
        startOffset: differenceInDays(start, minDate),
        totalWidth: differenceInDays(end, minDate) - differenceInDays(start, minDate),
        stagesBars,
      };
    });

    return {
      ganttOrders: processed,
      timelineStart: minDate,
      timelineEnd: maxDate,
      totalDays: days,
      uniqueStageNames: stageNameList,
      truncated: wasTruncated,
    };
  }, [orders]);

  if (!ganttOrders.length) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm" data-testid="gantt-empty">
        Nenhuma ordem com datas definidas para exibir no Gantt.
        <br />
        <span className="text-xs">Defina "Data de Inicio" e "Prazo de Entrega" nas ordens.</span>
      </div>
    );
  }

  // Build day markers for grid lines
  const dayMarkers = [];
  for (let d = 0; d <= totalDays; d++) {
    const date = addDays(timelineStart, d);
    const isWeekStart = date.getDay() === 1;
    const isMonthStart = date.getDate() === 1;
    dayMarkers.push({ date, offset: d, isWeekStart, isMonthStart });
  }

  // Header labels based on zoom
  const headerLabels = [];
  if (pxPerDay >= 60) {
    // Day view: show every day
    dayMarkers.forEach((m) => {
      headerLabels.push({ label: format(m.date, "dd"), sub: format(m.date, "EEE", { locale: ptBR }), offset: m.offset, isMonth: m.isMonthStart });
    });
  } else if (pxPerDay >= 30) {
    // Week view: show every 2 days
    dayMarkers.filter((_, i) => i % 2 === 0).forEach((m) => {
      headerLabels.push({ label: format(m.date, "dd/MM"), offset: m.offset, isMonth: m.isMonthStart });
    });
  } else if (pxPerDay >= 12) {
    // Month view: weekly
    dayMarkers.filter((m) => m.isWeekStart || m.offset === 0).forEach((m) => {
      headerLabels.push({ label: format(m.date, "dd/MM"), offset: m.offset, isMonth: m.isMonthStart });
    });
  } else {
    // Quarter: monthly
    dayMarkers.filter((m) => m.isMonthStart || m.offset === 0).forEach((m) => {
      headerLabels.push({ label: format(m.date, "MMM yy", { locale: ptBR }), offset: m.offset, isMonth: true });
    });
  }

  const ROW_HEIGHT = 44;
  const BAR_HEIGHT = 28;
  const LABEL_WIDTH = 300;
  const timelineWidth = totalDays * pxPerDay;
  const MIN_BAR_PX = Math.max(pxPerDay * 1.2, 20);

  const todayOffset = differenceInDays(startOfDay(new Date()), timelineStart);

  // Pre-compute grid line and weekend positions ONCE (shared across all rows).
  // Rendering these per-row previously caused O(orders * days) DOM nodes and OOM crashes.
  const gridMarkers = dayMarkers.filter((m) => pxPerDay >= 30 ? m.isWeekStart : m.isMonthStart);
  const weekendMarkers = pxPerDay >= 30
    ? dayMarkers.filter((m) => m.date.getDay() === 0 || m.date.getDay() === 6)
    : [];
  const rowsHeight = ganttOrders.length * ROW_HEIGHT;

  return (
    <div className="space-y-3" data-testid="gantt-chart">
      {/* Controls + Legend */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-3 text-xs">
          {uniqueStageNames.map((name) => (
            <div key={name} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colorForStage(name) }} />
              <span className="text-muted-foreground">{name}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 border rounded-md p-0.5" data-testid="gantt-zoom-controls">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={zoomIdx <= 0} onClick={() => setZoomIdx((i) => Math.max(0, i - 1))} data-testid="gantt-zoom-in">
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs font-medium px-2 min-w-[60px] text-center">{ZOOM_LEVELS[zoomIdx].label}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={zoomIdx >= ZOOM_LEVELS.length - 1} onClick={() => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))} data-testid="gantt-zoom-out">
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg bg-background" style={{ maxHeight: 520 }}>
        <div style={{ width: LABEL_WIDTH + timelineWidth + 20, minWidth: "100%" }}>
          {/* Timeline header */}
          <div className="flex border-b bg-muted/30 sticky top-0 z-20">
            <div className="shrink-0 px-3 py-2 text-xs font-semibold text-muted-foreground border-r sticky left-0 bg-muted/30 z-30" style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }}>
              Ordem
            </div>
            <div className="relative" style={{ width: timelineWidth, height: pxPerDay >= 60 ? 44 : 32 }}>
              {headerLabels.map((h, i) => (
                <div key={i} className="absolute flex flex-col items-start" style={{ left: h.offset * pxPerDay }}>
                  {h.isMonth && <div className="text-[10px] font-semibold text-foreground leading-tight">{pxPerDay >= 30 ? format(addDays(timelineStart, h.offset), "MMM yyyy", { locale: ptBR }) : ""}</div>}
                  <div className={`text-[10px] leading-tight mt-0.5 ${h.isMonth ? "font-semibold" : "text-muted-foreground"}`}>{h.label}</div>
                  {h.sub && <div className="text-[9px] text-muted-foreground/60 leading-tight">{h.sub}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Order rows */}
          <TooltipProvider delayDuration={100}>
            <div className="relative" style={{ height: rowsHeight }}>
              {/* Shared background: grid lines + weekend shading (rendered ONCE, not per row) */}
              <div className="absolute pointer-events-none" style={{ left: LABEL_WIDTH, top: 0, width: timelineWidth, height: rowsHeight }}>
                {gridMarkers.map((m, i) => (
                  <div key={`g${i}`} className="absolute top-0 bottom-0 border-l border-dashed border-muted/50" style={{ left: m.offset * pxPerDay }} />
                ))}
                {weekendMarkers.map((m, i) => (
                  <div key={`w${i}`} className="absolute top-0 bottom-0 bg-muted/20" style={{ left: m.offset * pxPerDay, width: pxPerDay }} />
                ))}
                {/* Today marker (shared) */}
                {todayOffset >= 0 && todayOffset <= totalDays && (
                  <div className="absolute top-0 bottom-0 z-20" style={{ left: todayOffset * pxPerDay }}>
                    <div className="w-0.5 h-full bg-red-500" />
                    <div className="absolute -top-0 -left-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
                  </div>
                )}
              </div>

              {ganttOrders.map((order) => (
              <div
                key={order.id}
                className={`flex border-b transition-colors ${hoveredOrder === order.id ? "bg-muted/40" : "hover:bg-muted/20"}`}
                onMouseEnter={() => setHoveredOrder(order.id)}
                onMouseLeave={() => setHoveredOrder(null)}
                data-testid={`gantt-row-${order.id}`}
                style={{ height: ROW_HEIGHT }}
              >
                {/* Order label */}
                <div className="shrink-0 px-3 flex items-center border-r sticky left-0 bg-background z-10" style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }}>
                  <span className="text-xs font-mono font-semibold truncate" title={`${order.order_code} - ${order.title}`}>{order.order_code || order.title || "-"}</span>
                </div>

                {/* Bars area */}
                <div className="relative flex items-center" style={{ width: timelineWidth, height: ROW_HEIGHT }}>
                  {/* Stage bars */}
                  {order.stagesBars.map((stage, idx) => {
                    const isDone = stage.status === "concluido";
                    const isInProgress = stage.status === "em_andamento";
                    const barWidth = Math.max(stage.width * pxPerDay, MIN_BAR_PX);
                    const barLeft = stage.startOffset * pxPerDay;
                    return (
                      <Tooltip key={stage.id || idx}>
                        <TooltipTrigger asChild>
                          <div
                            className={`absolute rounded-sm cursor-pointer transition-all hover:brightness-110 ${isDone ? "opacity-50" : ""}`}
                            style={{
                              left: barLeft,
                              width: barWidth,
                              height: BAR_HEIGHT,
                              top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                              backgroundColor: stage.color,
                              border: isInProgress ? "2px solid rgba(0,0,0,0.25)" : "none",
                              boxShadow: hoveredOrder === order.id ? "0 1px 4px rgba(0,0,0,0.15)" : "none",
                            }}
                          >
                            {barWidth > 35 && (
                              <span className="text-[10px] text-white font-medium px-1 truncate block leading-7" title={stage.name}>
                                {stage.name}
                              </span>
                            )}
                            {isDone && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs max-w-[200px]">
                          <p className="font-semibold">{stage.name}</p>
                          <p>{statusLabels[stage.status] || stage.status}</p>
                          <p className="text-muted-foreground">
                            {format(addDays(timelineStart, stage.startOffset), "dd/MM")} - {format(addDays(timelineStart, stage.startOffset + stage.width), "dd/MM")}
                          </p>
                          {stage.sub_stages?.length > 0 && (
                            <p>Sub-etapas: {stage.sub_stages.filter((ss) => ss.status === "concluido").length}/{stage.sub_stages.length}</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))}
            </div>
          </TooltipProvider>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
          <span>Hoje ({format(new Date(), "dd/MM/yyyy")})</span>
        </div>
        <span>
          {ganttOrders.length} ordens ativas
          {truncated && (
            <span className="ml-2 text-amber-600 font-medium">
              (exibindo apenas as {MAX_ORDERS_IN_GANTT} primeiras - use os filtros para refinar)
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
